import { useReactiveVar } from '@apollo/client';
import { purchaseVar } from 'apollo/cache';
import { PurchaseData } from 'storage/types';
import calculateSubTotal from 'utils/helpers/calculateSubTotal';
import { writeStoredPurchase } from 'storage/utils/purchaseStorage';

export const createNewPurchaseData = (date: string): PurchaseData => ({
  code: '',
  totalPrice: 0,
  packs: [],
  entrances: [],
  grill: null,
  canopy: null,
  clientId: '',
  paymentMethod: '',
  date,
});

const calculateItemsTotal = (purchase: PurchaseData): number => {
  const itemsTotal = [...purchase.packs, ...purchase.entrances].reduce(
    (total, item) => total + calculateSubTotal(item),
    0
  );
  const additionalServicesTotal = [purchase.grill, purchase.canopy].reduce(
    (total, activity) =>
      total + (activity ? (activity.value || 0) * activity.ticketCount : 0),
    0
  );
  return itemsTotal + additionalServicesTotal;
};

/** Non-reactive read, for event handlers. Components should use `usePurchase`. */
export const getPurchase = (): PurchaseData | null => purchaseVar();

/** Subscribes the component to every cart change. */
export const usePurchase = (): PurchaseData | null => useReactiveVar(purchaseVar);

/**
 * The only writer. `totalPrice` always means "sum of the items" — promotion codes and
 * gift cards are applied at checkout and never persisted into it.
 */
export const setPurchase = (purchase: PurchaseData): PurchaseData => {
  const stored: PurchaseData = {
    ...purchase,
    totalPrice: calculateItemsTotal(purchase),
  };
  writeStoredPurchase(stored);
  purchaseVar(stored);
  return stored;
};

/** Reads the cart without touching it, falling back to an empty one on `date`. */
export const getOrCreatePurchase = (date: string): PurchaseData =>
  getPurchase() || createNewPurchaseData(date);

/** Drops the cart and starts a new one. The only way a stored cart is discarded. */
export const startPurchaseOnDate = (date: string): PurchaseData =>
  setPurchase(createNewPurchaseData(date));

/** Drops one pack or entrance from the cart, leaving the rest untouched. */
export const removePurchaseItem = (
  item: 'packs' | 'entrances',
  id: string
): PurchaseData | null => {
  const purchase = getPurchase();
  if (!purchase) return null;
  const next: PurchaseData = { ...purchase };
  next[item] = purchase[item].filter((entry) => entry?.id !== id);
  return setPurchase(next);
};
